import type { Plan } from '@obv/api-client';
import {
  EVERYONE, addDays, addMonths, comparePlans, formatTime, monthGrid, monthLabel, ordinal, ordinalDate, plansForDay, weekdayHeaders, weekdayOf, WEEKDAYS,
} from '@obv/core';
import { personColor } from '@obv/ui';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { PLAN_WINDOW_AHEAD, PLAN_WINDOW_BACK, usePlanWindow } from '@/queries/hooks';
import { memberLabel, useDisplay } from '../context';
import { Card } from '../ui';

const AGENDA_DAYS = 7;

/**
 * Full calendar, live: today's date and time, the coming days' plans grouped Today / Tomorrow /
 * weekday, and a month grid (today filled, days with plans dotted; tap a day to see its plans).
 * `narrow` slots show only the month and the selected day.
 */
export function CalendarWidget({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const today = ctx.clock.dateKey;
  const windowQ = usePlanWindow(today, ctx.boot.server_time);
  const items = windowQ.data ? windowQ.data.items.filter((p) => !p.deleted_at) : [];
  const [month, setMonth] = useState(today);
  const [selected, setSelected] = useState(today);
  // A new day moves the calendar along with it.
  useEffect(() => {
    setMonth(today);
    setSelected(today);
  }, [today]);

  const byDay: Record<string, Plan[]> = {};
  for (const p of items) (byDay[p.date] = byDay[p.date] || []).push(p);
  for (const k of Object.keys(byDay)) byDay[k]!.sort(comparePlans);

  // Plans are cached for the navigable window around today; don't page past it.
  const minMonth = addDays(today, -PLAN_WINDOW_BACK).slice(0, 7);
  const maxMonth = addDays(today, PLAN_WINDOW_AHEAD).slice(0, 7);
  const prevMonth = addMonths(month, -1);
  const nextMonth = addMonths(month, 1);

  // Narrow slots get a short heading ("Sun 18th Oct") so the time still fits beside it.
  const dayNum = Number(today.slice(8));
  const heading = narrow ? `${WEEKDAYS[weekdayOf(today)]!.slice(0, 3)} ${ordinal(dayNum)} ${monthLabel(today).slice(0, 3)}` : ordinalDate(today);
  const head = (
    <div className="flex flex-none items-baseline justify-between">
      <button type="button" className="tap -my-2 h-[44px] min-w-0 text-left" onClick={() => navigate('/display/calendar')} aria-label={`${heading}, open calendar`}>
        <h2 className="ellipsis text-[18px] font-semibold">{heading} ↗</h2>
      </button>
      <span className="tabular ml-2 flex-none text-[15px] text-muted2">{formatTime(ctx.clock.hhmm, ctx.prefs.h24)}</span>
    </div>
  );

  const monthBlock = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex flex-none items-center justify-between">
        <NavButton label="Previous month" disabled={prevMonth.slice(0, 7) < minMonth} onClick={() => setMonth(prevMonth)}>
          ‹
        </NavButton>
        <button
          type="button"
          className="tap h-[36px] whitespace-nowrap px-1 font-medium"
          style={{ fontSize: narrow ? 14 : 15 }}
          onClick={() => {
            setMonth(today);
            setSelected(today);
          }}
          aria-label="Back to today"
        >
          {monthLabel(month)}
        </button>
        <NavButton label="Next month" disabled={nextMonth.slice(0, 7) > maxMonth} onClick={() => setMonth(nextMonth)}>
          ›
        </NavButton>
      </div>
      <MonthGrid anchor={month} today={today} selected={selected} byDay={byDay} onSelect={setSelected} compact={narrow} />
      {narrow ? null : <SelectedDay date={selected} today={today} plans={byDay[selected] || []} />}
    </div>
  );

  if (narrow) {
    return (
      <Card tone={5} className="flex flex-col overflow-hidden px-4 py-4">
        {head}
        <div className="mt-2 flex min-h-0 flex-1 flex-col">{monthBlock}</div>
      </Card>
    );
  }

  const agendaDays = Array.from({ length: AGENDA_DAYS }, (_, i) => addDays(today, i)).filter((d, i) => i === 0 || (byDay[d] || []).length > 0);
  return (
    <Card tone={5} className="grid overflow-hidden" style={{ gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)' }}>
      <div className="flex min-h-0 min-w-0 flex-col py-5 pl-6 pr-4">
        {head}
        <div className="scroll-y mt-2 min-h-0 flex-1">
          {agendaDays.map((d, i) => (
            <AgendaGroup key={d} label={i === 0 ? 'Today' : d === addDays(today, 1) ? 'Tomorrow' : WEEKDAYS[weekdayOf(d)]!} plans={byDay[d] || []} />
          ))}
        </div>
      </div>
      <div className="flex min-h-0 min-w-0 flex-col border-l border-line py-4 pl-4 pr-5">{monthBlock}</div>
    </Card>
  );
}

function NavButton({ children, label, disabled, onClick }: { children: string; label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className="tap flex h-[36px] w-[36px] items-center justify-center text-[20px] disabled:opacity-25">
      {children}
    </button>
  );
}

function AgendaGroup({ label, plans }: { label: string; plans: Plan[] }) {
  const ctx = useDisplay();
  return (
    <div className="mb-2">
      <div className="pb-1 pt-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">{label}</div>
      {plans.length === 0 ? <div className="pb-1 text-[14px] text-muted">Nothing planned.</div> : null}
      {plans.map((p) => {
        const who = memberLabel(ctx, p.member_id);
        return (
          <div key={p.id} className="flex py-[5px]" style={{ opacity: p.completed ? 0.5 : 1 }}>
            <span className="mr-3 w-[4px] flex-none rounded-[2px]" style={{ background: personColor(who.colorKey) }} />
            <span className="min-w-0">
              <span className="ellipsis block text-[15px] font-semibold" style={{ textDecoration: p.completed ? 'line-through' : 'none' }}>
                {p.title}
              </span>
              <span className="block text-[13px] text-muted2">
                {p.all_day ? 'All day' : formatTime(p.time!, ctx.prefs.h24)} · {who.name}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MonthGrid({
  anchor, today, selected, byDay, onSelect, compact,
}: {
  anchor: string;
  today: string;
  selected: string;
  byDay: Record<string, Plan[]>;
  onSelect: (d: string) => void;
  compact: boolean;
}) {
  const ctx = useDisplay();
  const rows = monthGrid(anchor, ctx.prefs.week_starts);
  const heads = weekdayHeaders(ctx.prefs.week_starts);
  return (
    <div className="mt-1 flex-none">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
        {heads.map((h) => (
          <div key={h} className="py-1 text-center text-[11px] uppercase tracking-[0.06em] text-muted">
            {h.slice(0, 2)}
          </div>
        ))}
        {rows.flat().map((c) => {
          const isToday = c.date === today;
          const isSel = c.date === selected && !isToday;
          const has = (byDay[c.date] || []).length > 0;
          return (
            <button
              key={c.date}
              type="button"
              onClick={() => onSelect(c.date)}
              aria-label={`${c.date}${has ? ', has plans' : ''}${isToday ? ', today' : ''}`}
              aria-pressed={c.date === selected}
              className="tap flex flex-col items-center justify-center"
              style={{ height: compact ? 25 : 32, opacity: c.inMonth ? 1 : 0.35 }}
            >
              <span
                className="tabular flex h-[24px] w-[24px] items-center justify-center rounded-full text-[13px]"
                style={
                  isToday
                    ? { background: 'var(--ink)', color: 'var(--onInk)', fontWeight: 700 }
                    : isSel
                      ? { boxShadow: 'inset 0 0 0 1.5px var(--ink)', fontWeight: 600 }
                      : undefined
                }
              >
                {c.day}
              </span>
              <span className="mt-[1px] h-[4px] w-[4px] rounded-full" style={{ background: has ? 'var(--now)' : 'transparent' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectedDay({ date, today, plans }: { date: string; today: string; plans: Plan[] }) {
  const ctx = useDisplay();
  const label = date === today ? 'Today' : date === addDays(today, 1) ? 'Tomorrow' : ordinalDate(date);
  const list = plansForDay(plans, date, EVERYONE);
  return (
    <div className="scroll-y mt-2 min-h-0 flex-1 border-t border-line pt-2">
      <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted">{label}</div>
      {list.length === 0 ? <div className="mt-1 text-[13px] text-muted">Nothing planned.</div> : null}
      {list.map((p) => (
        <div key={p.id} className="mt-1 flex items-center text-[13px]" style={{ opacity: p.completed ? 0.5 : 1 }}>
          <span className="mr-2 h-[6px] w-[6px] flex-none rounded-full" style={{ background: personColor(memberLabel(ctx, p.member_id).colorKey) }} />
          <span className="tabular mr-2 flex-none text-muted2">{p.all_day ? 'All day' : formatTime(p.time!, ctx.prefs.h24)}</span>
          <span className="ellipsis font-medium">{p.title}</span>
        </div>
      ))}
    </div>
  );
}
